import axios from 'axios';

// Environment-aware logging - silent in production
const isDev = process.env.NODE_ENV !== 'production';
const log = (...args) => isDev && console.log(...args);

const IMAGE_API_URL = process.env.NODE_ENV === 'production'
  ? '/api/generate-image'
  : `http://${window.location.hostname}:3001/api/generate-image`;

const PROMPT_API_URL = process.env.NODE_ENV === 'production'
  ? '/api/generate-image-prompt'
  : `http://${window.location.hostname}:3001/api/generate-image-prompt`;

// Get the current theme from localStorage (fallback to noir)
const getCurrentTheme = () => {
  try {
    return localStorage.getItem('ribbonRiderTheme') || 'noir';
  } catch (e) {
    return 'noir';
  }
};

// Parse Claude's response to extract scene details for image generation
export const parseSceneForImage = (claudeResponse, missionContext = {}) => {
  const response = claudeResponse.toLowerCase();
  
  // Determine panel type based on content
  let panelType = 'dialogue';
  if (response.includes('mission complete') || response.includes('mission failed')) {
    panelType = 'splash';
  } else if (response.includes('suddenly') || response.includes('quickly') || response.includes('run') || response.includes('fight')) {
    panelType = 'action';
  } else if (response.includes('realize') || response.includes('shocked') || response.includes('surprised')) {
    panelType = 'reaction';
  } else if (response.includes('arrive') || response.includes('enter') || response.includes('the year is') || response.includes('you find yourself')) {
    panelType = 'establishing';
  }
  
  // Determine mood based on content
  let mood = 'mystery';
  if (response.includes('danger') || response.includes('death') || response.includes('killed') || response.includes('disaster')) {
    mood = 'danger';
  } else if (response.includes('success') || response.includes('hope') || response.includes('saved')) {
    mood = 'hope';
  } else if (response.includes('tension') || response.includes('nervous') || response.includes('worried')) {
    mood = 'tension';
  } else if (response.includes('power') || response.includes('important') || response.includes('critical')) {
    mood = 'power';
  }
  
  return {
    panelType,
    mood,
    year: missionContext.year || 2000
  };
};

// Get Claude to generate a clean image prompt from story text
const getImagePromptFromClaude = async (storyText, year, mood, isRetry = false, theme = null) => {
  const currentTheme = theme || getCurrentTheme();
  
  try {
    const response = await axios.post(PROMPT_API_URL, {
      storyText,
      year,
      mood,
      isRetry,
      theme: currentTheme
    });
    return response.data.imagePrompt;
  } catch (error) {
    if (isDev) console.error('Error getting image prompt from Claude:', error);
    // Fallback to a generic prompt based on theme
    const fallbackStyles = {
      noir: 'noir comic book style, high contrast black and white, dramatic shadows, halftone dots',
      popArt: 'pop art comic book style, bold primary colors, Ben-Day dots pattern, thick black outlines',
      manga: 'manga comic style, black and white with screentone shading, speed lines, clean linework',
      vintage: 'vintage 1940s golden age comic style, warm sepia tones, aged paper texture'
    };
    return `dramatic comic panel, vintage setting from ${year}, ${fallbackStyles[currentTheme] || fallbackStyles.noir}`;
  }
};

// Main function to generate an image with retry logic for content violations
export const generateComicImage = async (prompt, storyText = null, year = 2000, mood = 'mystery', retryCount = 0, theme = null) => {
  const MAX_RETRIES = 2;
  const currentTheme = theme || getCurrentTheme();
  
  try {
    const response = await axios.post(IMAGE_API_URL, {
      prompt,
      width: 1024,
      height: 1024
    });
    
    return {
      success: true,
      imageUrl: response.data.imageUrl,
      revisedPrompt: response.data.revisedPrompt
    };
  } catch (error) {
    if (isDev) console.error('Error generating image:', error);
    
    // Check if this is a content policy violation (451) and we can retry
    const isContentViolation = error.response?.status === 451;
    
    if (isContentViolation && retryCount < MAX_RETRIES && storyText) {
      log(`Content policy violation - attempting retry ${retryCount + 1}/${MAX_RETRIES}`);
      
      // Get a new, safer prompt from Claude
      const saferPrompt = await getImagePromptFromClaude(storyText, year, mood, true, currentTheme);
      log('Retry with safer prompt:', saferPrompt);
      
      // Retry with the new prompt
      return generateComicImage(saferPrompt, storyText, year, mood, retryCount + 1, currentTheme);
    }
    
    return {
      success: false,
      error: error.response?.data?.error || error.message,
      isContentViolation
    };
  }
};

// Generate image for a game panel - now uses Claude to create the prompt with theme awareness
export const generatePanelImage = async (claudeResponse, missionContext = {}) => {
  const sceneDetails = parseSceneForImage(claudeResponse, missionContext);
  const year = missionContext.year || sceneDetails.year;
  const mood = missionContext.mood || sceneDetails.mood;
  const theme = missionContext.theme || getCurrentTheme();
  
  // Use Claude to generate a clean image prompt
  const prompt = await getImagePromptFromClaude(claudeResponse, year, mood, false, theme);
  
  log('Generated image prompt:', prompt);
  log('Using theme:', theme);
  
  // Pass story text so we can retry with a safer prompt if needed
  return generateComicImage(prompt, claudeResponse, year, mood, 0, theme);
};

// Legacy function for compatibility - no longer used but kept for reference
export const buildImagePrompt = ({ sceneDescription, panelType, mood, year }) => {
  return `${sceneDescription}, noir comic book style, high contrast black and white, dramatic shadows, halftone dots, no people, no text`;
};

// Named export object for convenience
const imageGenerationService = {
  generateComicImage,
  generatePanelImage,
  buildImagePrompt,
  parseSceneForImage,
  getCurrentTheme
};

export default imageGenerationService;